import { Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { ChargingUser } from '../charging-user.model';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { SnackbarService } from 'ok-angular-shared/components';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTag, IdTagSearchEvent, LookupIdTag } from '../../id-tags/id-tag-model';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { PageEvent } from '@angular/material/paginator';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  interval,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NumberOnlyHelper } from 'src/app/shared/helpers/number-only';
import { ChargingUserService, CreateChargingUserIdTagRequest } from 'src/app/shared/services/charging-user.service';
import OrganizationService from 'src/app/shared/services/organization.service';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ChargingUserEditModalComponent, ChargingUserEditModalData } from '../charging-user-edit-modal/charging-user-edit-modal.component';
import { FeedbackInfoComponent, FeedbackFailComponent } from 'ok-angular-shared/components';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface IChargingUserIdTagsModalData {
  chargingUser: ChargingUser;
}

export interface IdTagFormGroup {
  selectedPrintedId: FormControl<number | null>;
  printedId: FormControl<number | null>;
  idToken: FormControl<string | null>;
  idTagIdentifier: FormControl<string | null>;
}

@Component({
  selector: 'adm-charging-user-idtags-modal',
  templateUrl: './charging-user-idtags-modal.component.html',
  styleUrl: './charging-user-idtags-modal.component.scss',
  imports: [
    CustomDropDownArrowDirective,
    FeedbackInfoComponent,
    FeedbackFailComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIconButton,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    MatHint,
    MatError,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    ReactiveFormsModule,
    MatTooltipModule,
  ],
})
export class ChargingUserIdTagsModalComponent {
  dialogRef = inject<MatDialogRef<ChargingUserIdTagsModalComponent>>(MatDialogRef);
  data = inject<IChargingUserIdTagsModalData>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  private organizationService = inject(OrganizationService);
  private dialog = inject(MatDialog);

  public chargingUser: ChargingUser = this.data?.chargingUser;
  public chargingUserUpdated: ChargingUser | null = null;
  public idTags = signal<IdTag[]>([]);
  public readonly hasCompanyCar = this.chargingUser.idTagUserType.toString() === IdTagUserType[IdTagUserType.CompanyCar].toString();
  public readonly companyCarDetermined =
    this.chargingUser.idTagUserType.toString() !== IdTagUserType[IdTagUserType.NotDetermined].toString();
  public organizationHasIdTagGroup = true;
  public idTagStatusEnum = IdTagStatusEnum;
  public pageEvent: PageEvent = new PageEvent();
  public idTagSearchEvent: IdTagSearchEvent = { chargingUserId: this.chargingUser.id, paging: this.pageEvent } as IdTagSearchEvent;
  public idTagFormGroup = new FormGroup<IdTagFormGroup>({
    selectedPrintedId: new FormControl<number | null>(null, [Validators.required]),
    printedId: new FormControl<number | null>(null, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
      Validators.minLength(1),
      Validators.maxLength(6),
    ]),
    idToken: new FormControl<string | null>(null, [Validators.required]),
    idTagIdentifier: new FormControl<string | null>(null),
  });
  public selectedPrintedId: number | null = null;
  public printedIdAlreadyUsed = false;
  public queriedPrintedId: number | null = null;
  public foundNoPrintedIds: string | null = null;
  public filteredLookupIdTags: LookupIdTag[];
  public savingIdTag = false;
  public readonly chargingUserHasDepartment: boolean = this.chargingUser.departmentId > 0;
  public readonly hasDeliveryAddress = this.chargingUser.idTagShippingAddress?.length > 0;
  private readonly $refreshIdTags = new Subject<void>();

  constructor() {
    this.organizationService
      .getOrganization(this.chargingUser.organizationId)
      .pipe(
        tap(organization => {
          this.organizationHasIdTagGroup = organization && organization.idTagGroupName !== null;
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.idTagFormGroup.controls.printedId.valueChanges
      .pipe(
        startWith(null), // let one blank through to search for all
        debounceTime(300),
        distinctUntilChanged(),
        filter(
          (val): val is number => val !== null && NumberOnlyHelper.isNumber(val) && val.toString() !== this.queriedPrintedId?.toString()
        ),
        switchMap(val => {
          this.queriedPrintedId = val;
          this.printedIdAlreadyUsed = false;
          this.foundNoPrintedIds = null;
          return this.idTagService.lookupIdTags(val).pipe(
            tap(filteredLookupIdTags => {
              this.filteredLookupIdTags = filteredLookupIdTags;
              if (this.selectedPrintedId !== this.queriedPrintedId) {
                this.idTagFormGroup.patchValue({
                  selectedPrintedId: null,
                  idToken: null,
                  idTagIdentifier: null,
                });
              }
              if (filteredLookupIdTags.length === 0) {
                this.printedId.setErrors({ foundNoPrintedIds: true });
                this.foundNoPrintedIds = `Der blev ikke fundet nogen brik ID'er`;
              }
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    combineLatest([this.$refreshIdTags.pipe(startWith(void 0)), this.idTagService.getIdTags(this.idTagSearchEvent)])
      .pipe(
        tap(([_, x]) => this.idTags.set(x.items)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public async selectIdTag(value: number | null) {
    this.selectedPrintedId = value;

    if (this.selectedPrintedId) {
      const idTagSearchEvent: IdTagSearchEvent = new IdTagSearchEvent(new PageEvent());
      idTagSearchEvent.printedId = this.selectedPrintedId;
      const existingIdTagsWithPrintedId = await firstValueFrom(this.idTagService.getIdTags(idTagSearchEvent));
      this.printedIdAlreadyUsed = existingIdTagsWithPrintedId.items.length > 0;

      if (this.printedIdAlreadyUsed) {
        this.queriedPrintedId = this.selectedPrintedId;
        this.selectedPrintedId = null;
        this.idTagFormGroup.controls.idToken.setValue(null);
        this.printedId.setErrors({ printedIdAlreadyUsed: true });
      } else {
        const selectedIdTag = this.filteredLookupIdTags.find(i => i.printedId === this.selectedPrintedId);
        if (!selectedIdTag) {
          return;
        }

        this.idTagFormGroup.controls.selectedPrintedId.setValue(selectedIdTag.printedId);
        this.idTagFormGroup.controls.idToken.setValue(selectedIdTag.idToken);
        this.idTagFormGroup.controls.idTagIdentifier.setValue(selectedIdTag.idTagIdentifier);
      }
    }
  }

  public async createIdTag() {
    this.savingIdTag = true;
    const printedId = this.idTagFormGroup.controls.printedId.value;
    if (printedId == null) {
      this.savingIdTag = false;
      return;
    }

    const idTag: CreateChargingUserIdTagRequest = {
      departmentId: null,
      idTagIdentifier: this.idTagFormGroup.controls.idTagIdentifier.value ?? '',
      shippingAddress: this.chargingUser.idTagShippingAddress ?? '',
      description: this.chargingUser.name ?? '',
      attention: this.chargingUser.name ?? '',
      printedId,
      idToken: this.idTagFormGroup.controls.idToken.value ?? '',
      chargingUserId: this.chargingUser.id,
    };

    this.chargingUserService
      .createIdTagForChargingUser(idTag)
      .pipe(
        switchMap(idTagId => this.idTagService.getIdTag(idTagId)),
        tap(newIdTag => {
          this.idTags.update(tags => [...tags, newIdTag]);
          this.idTagFormGroup.reset();
        }),
        switchMap(() =>
          this.chargingUserService.getChargingUser(this.chargingUser.id).pipe(
            tap(chargingUserUpdated => {
              this.chargingUserUpdated = chargingUserUpdated;
              this.chargingUser = this.chargingUserUpdated;
              this.snackbarService.success('Brikken blev succesfuldt oprettet.');
            })
          )
        ),
        catchError(err => {
          if (err?.error.code === 300007) {
            this.snackbarService.error('Der er sket en fejl i CS management - Der kan ikke være flere stier fra lokation til brik.');
          } else {
            this.snackbarService.error('Der skete en fejl under oprettelse af brikken.');
          }
          return [];
        })
      )
      .subscribe()
      .add(() => {
        interval(700).subscribe(() => {
          this.savingIdTag = false;
        });
      });
  }

  get printedId() {
    return this.idTagFormGroup.controls.printedId;
  }

  get idToken() {
    return this.idTagFormGroup.controls.idToken;
  }

  public async deleteIdTag(idTag: IdTag) {
    idTag.status = IdTagStatusEnum.NoIdTagGroup;
    this.idTagService.setStatus(idTag).subscribe({
      next: () => {
        this.$refreshIdTags.next();
        this.chargingUserUpdated = this.chargingUser;
      },
      error: err => {
        this.snackbarService.error(`Der skete fejl under sletning (${err.error.message})`);
      },
    });
  }

  public close() {
    this.dialogRef.close({ chargingUserUpdated: this.chargingUserUpdated });
    this.chargingUserUpdated = null;
  }

  getErrorMessage(controlName: string) {
    if (controlName === 'printedId' && this.idTagFormGroup.controls['printedId'].hasError('required')) {
      return `Indtast venligst et brik ID (helt tal)`;
    }
    if (controlName === 'idToken' && this.idTagFormGroup.controls['idToken'].hasError('required')) {
      return `Intet RFID - fremsøg og vælg en brik ID <br />for at få indsat et RFID`;
    }
    return '';
  }

  public openEditUserModal(chargingUser: ChargingUser): void {
    const dialogRef = this.dialog.open(ChargingUserEditModalComponent, {
      data: { chargingUserId: chargingUser.id } as ChargingUserEditModalData,
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result?.chargingUserUpdated) {
        this.chargingUserUpdated = await firstValueFrom(this.chargingUserService.getChargingUser(result.chargingUserUpdated.id));
        this.chargingUser = this.chargingUserUpdated;
        this.$refreshIdTags.next();
      }
    });
  }
}
